import L from "leaflet";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Marker, Popup } from "react-leaflet";
import { useTrackingStore } from "../store";
import type { VehicleMarkerProps } from "../types";
import { formatDate } from "../utils";
import { VehicleCard } from "./cards";

export const VehicleMarker = memo(
	({ telemetry, onVehicleSelect }: VehicleMarkerProps) => {
		const { selectedVehicleId } = useTrackingStore();
		const markerRef = useRef<L.Marker | null>(null);

		const handleClick = useCallback(() => {
			if (onVehicleSelect) {
				onVehicleSelect(telemetry);
			}
		}, [telemetry, onVehicleSelect]);

		useEffect(() => {
			if (selectedVehicleId === telemetry.vehicleId && markerRef.current) {
				const marker = markerRef.current;
				marker.openPopup?.();
			}
		}, [selectedVehicleId, telemetry.vehicleId]);

		const isMoving = telemetry.moving;
		const iconUrl = isMoving
			? "/tracking/card-moving.svg"
			: "/tracking/card-stopped.svg";

		const customIcon = useMemo(
			() =>
				L.divIcon({
					html: `<img src="${iconUrl}" width="32" height="32" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));" />`,
					className: "custom-vehicle-icon",
					iconSize: [32, 32],
					iconAnchor: [16, 16],
				}),
			[iconUrl]
		);

		return (
			<Marker
				eventHandlers={{
					click: handleClick,
				}}
				icon={customIcon}
				position={[
					Number.parseFloat(telemetry.latitude),
					Number.parseFloat(telemetry.longitude),
				]}
				ref={markerRef}
			>
				<Popup className="custom-popup" maxHeight={180}>
					<VehicleCard
						data={{
							latitude: telemetry.latitude,
							longitude: telemetry.longitude,
							moving: telemetry.moving,
							vehiclePlate: telemetry.plate,
							vehicleId: telemetry.vehicleId,
							lastUpdate: formatDate(telemetry.dateTelemetry),
							driver: telemetry.driver?.name || "N/A",
							address: telemetry.address ?? undefined,
							trailerPlates: (telemetry.trailers ?? []).map(
								(trailer) => trailer.plate
							),
						}}
						style={{ padding: 0, background: "none", border: "none" }}
					/>
				</Popup>
			</Marker>
		);
	}
);
